import React from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { safeNavItems, getNavItemKey } from '../utils/navItemUtils';
import {
	classifyMegaMenuItem,
	resolveHeadingLabel
} from '../utils/megaMenuColumnUtils';
import MegaMenuLink from './MegaMenuLink';
import MegaMenuFragment from './MegaMenuFragment';

/**
 * MegaMenuGroup — a headed group of links (and/or embedded fragments) within a
 * mega menu column. Always renders a heading: its own label if it has one,
 * otherwise the parent featured item's label (via resolveHeadingLabel).
 *
 * @param {object} props
 * @param {NavigationItem} props.item
 * @param {string} [props.parentLabel] - Fallback heading when the group has no
 *   label of its own.
 * @param {() => void} props.onLinkClick
 * @param {boolean} [props.isFullWidth] - Spread the group's link children across
 *   the full-width 3-column grid.
 * @param {boolean} [props.standalone] - True when this group is the sole content
 *   of its column; a single link child is then width-capped.
 * @param {string} [props.skeletonHeight] - Skeleton height for embedded children.
 */
const MegaMenuGroup = ({
	item,
	parentLabel = undefined,
	onLinkClick,
	isFullWidth = false,
	standalone = false,
	skeletonHeight = undefined
}) => {
	const heading = resolveHeadingLabel(item, parentLabel);
	const children = safeNavItems(item);
	// A lone link is width-capped only when the group is the sole column content.
	const childIsStandalone = standalone && children.length === 1;

	return (
		<div
			data-testid="mega-menu-group"
			className={setClassNames([
				'ws-nav-drawer__mega-menu-group',
				isFullWidth && 'ws-nav-drawer__mega-menu-group--full-width'
			])}
		>
			{heading && (
				<strong className="ws-nav-drawer__mega-menu-section-heading pt-6 pb-4 h4">
					{heading}
				</strong>
			)}
			<div
				data-testid="mega-menu-group-children"
				className="ws-nav-drawer__mega-menu-group__children"
			>
				{children.map((child, index) =>
					classifyMegaMenuItem(child) === 'fragment' ? (
						<MegaMenuFragment
							key={getNavItemKey(child, index)}
							url={child.url}
							skeletonHeight={skeletonHeight}
						/>
					) : (
						<MegaMenuLink
							key={getNavItemKey(child, index)}
							item={child}
							onLinkClick={onLinkClick}
							standalone={childIsStandalone}
						/>
					)
				)}
			</div>
		</div>
	);
};

MegaMenuGroup.propTypes = {
	item: PropTypes.shape({
		label: PropTypes.string,
		navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	parentLabel: PropTypes.string,
	onLinkClick: PropTypes.func.isRequired,
	isFullWidth: PropTypes.bool,
	standalone: PropTypes.bool,
	skeletonHeight: PropTypes.string
};

MegaMenuGroup.defaultProps = {
	parentLabel: undefined,
	isFullWidth: false,
	standalone: false,
	skeletonHeight: undefined
};

export default MegaMenuGroup;
